"use client";

import { NextIntlClientProvider, IntlErrorCode } from "next-intl";
import { ReactNode } from "react";

interface IntlProviderProps {
  children: ReactNode;
  messages: Record<string, unknown>;
  locale: string;
}

/**
 * Custom error handler for next-intl to gracefully handle missing translations
 */
function handleIntlError(error: { code: IntlErrorCode; message: string }) {
  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    // Log missing messages in development, but don't crash
    if (process.env.NODE_ENV === "development") {
      console.warn("Missing translation:", error.message);
    }
    return;
  }
  // Log other errors but don't throw to prevent crashes
  console.error("Intl error:", error.code, error.message);
}

/**
 * Provide fallback for missing messages
 */
function getMessageFallback({ namespace, key }: { namespace?: string; key: string }) {
  return namespace ? `${namespace}.${key}` : key;
}

/**
 * Client-side IntlProvider wrapper with error handling
 * This prevents translation errors from crashing the entire application
 */
export function IntlProvider({ children, messages, locale }: IntlProviderProps) {
  return (
    <NextIntlClientProvider
      messages={messages}
      locale={locale}
      onError={handleIntlError}
      getMessageFallback={getMessageFallback}
    >
      {children}
    </NextIntlClientProvider>
  );
}
